/* Moteur du panier — Scoop by Coralie, lot 104.

   RÉSULTAT ATTENDU
   ----------------
   Ce fichier est la SEULE porte du thème vers `/cart/*`. Rien d'autre dans le
   thème n'écrit dans le panier. Chaque mutation passe par une file : elle est
   appliquée, le panier réel est relu, la décision de réconciliation est jouée
   jusqu'à ce qu'elle soit vide, la section panier est rendue une fois, et un
   seul événement `sbc:cart-updated` (+ `sbc:cadeaux`, qui porte l'état des
   cadeaux) annonce le résultat.

   POURQUOI CETTE FORME — ce qu'elle remplace
   ------------------------------------------
   Avant le lot 104, trois fichiers remplaçaient `window.fetch` (moteur de
   cadeaux, tiroir, normalisation de la vidéo) et un quatrième remplaçait
   `XMLHttpRequest.prototype.open/send`. Chacun observait les écritures des
   autres, et l'ordre d'exécution dépendait de l'ordre des `defer` dans
   `layout/theme.liquid`. La coordination était confiée à des minuteries :
   700 ms avant d'ouvrir le tiroir, 250 ms avant de réconcilier, 180 ms avant
   d'envoyer une quantité, 220 ms, 120 ms au retour d'onglet, 150 ms au focus.
   Un ajout déclenchait une chaîne de huit à quinze requêtes ; deux
   réconciliations pouvaient se croiser, et un `signature()` partiel servait de
   garde.

   Trois raisons de remplacer cela par une file explicite :

   1. **Une horloge n'est pas une cause.** « 700 ms après l'ajout » suppose que
      la réconciliation aura fini ; elle n'en sait rien. Ici, le tiroir s'ouvre
      parce que le moteur a fini, pas parce qu'un délai est écoulé. Le seul
      `setTimeout` restant de ce fichier est un debounce de SAISIE, nommé, qui
      groupe les clics rapides sur « + » — il ne coordonne rien.

   2. **Un wrapper global voit tout le monde.** Les scripts tiers de la boutique
      (Judge.me, applications d'avis, pixels) passent par `fetch` : ils
      déclenchaient nos réconciliations, et nos réconciliations passaient par
      leurs propres wrappers. Un moteur appelé explicitement ne peut pas être
      pris pour un tiers, ni prendre un tiers pour lui.

   3. **Une file rend l'ordre lisible.** Deux clics rapides ne peuvent plus
      produire deux réconciliations concurrentes : la seconde attend la
      première et travaille sur l'état réel qu'elle a laissé.

   CE QUE LE MOTEUR NE FAIT PAS
   ----------------------------
   Il ne décide rien : la règle métier vit dans `assets/sbc-panier-decision.js`,
   pure et testée par `node tools/verif-panier.test.mjs`. Il ne peint rien : le
   bloc de progression est peint par `assets/sbc-cadeaux.js`, le résumé est
   rendu en Liquid par `snippets/sbc-cart-resume.liquid` (lot 103).
*/
(() => {
  const noeudConfig = document.querySelector('[data-sbc-cadeaux-config]');
  const DECISION = window.SbcPanierDecision || null;

  const RACINE = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const url = (chemin) => `${RACINE}${chemin}`.replace(/([^:]\/)\/+/g, '$1');

  let CFG = null;
  if (noeudConfig) {
    try { CFG = JSON.parse(noeudConfig.textContent); } catch (_) { CFG = null; }
  }
  /* Sans configuration ni module de décision, le moteur reste un simple
     passe-plat : il applique ce qu'on lui demande et publie le panier. Il
     n'invente aucun cadeau — le sens sûr de l'erreur. */
  const RECONCILIE = Boolean(CFG && DECISION && Array.isArray(CFG.produitsEligibles));

  /* Référence capturée au chargement. Le thème n'installe plus aucun wrapper ;
     cette capture protège seulement des scripts tiers qui, eux, en installent. */
  const requete = window.fetch.bind(window);

  const JSON_ENTETES = { 'Content-Type': 'application/json', Accept: 'application/json' };

  const lireReponse = async (reponse) => {
    let corps = null;
    try { corps = await reponse.json(); } catch (_) { corps = null; }
    if (reponse.ok) return corps;
    const erreur = new Error(
      (corps && (corps.description || corps.message)) || 'Mise à jour du panier impossible'
    );
    erreur.reponse = corps;
    throw erreur;
  };

  const poster = (chemin, corps) => requete(url(chemin), {
    method: 'POST', headers: JSON_ENTETES, body: JSON.stringify(corps)
  }).then(lireReponse);

  const lirePanier = () => requete(url('cart.js'), {
    headers: { Accept: 'application/json' }, cache: 'no-store'
  }).then(lireReponse);

  /* --------------------------------------------------------------------
     Rendu de la section panier.

     Une seule fois par cycle, et seulement si l'affichage est en retard sur
     le panier réel. La signature compare ce que le DOM montre à ce que
     `/cart.js` dit ; `sections/main-cart.liquid` l'écrit dans
     `data-sbc-cart-state`.
     -------------------------------------------------------------------- */
  const signature = (panier) => `${panier.item_count}:${panier.total_price}:` +
    panier.items.map((l) => `${l.variant_id}x${l.quantity}x${l.final_line_price}`).join(',') + ',';

  const domEnRetard = (panier) => {
    const racine = document.querySelector('[data-sbc-cart-section]');
    if (!racine) return false;
    return racine.getAttribute('data-sbc-cart-state') !== signature(panier);
  };

  const rendreSection = async () => {
    const racineSection = document.querySelector('[data-sbc-cart-section]');
    if (!racineSection) return;
    const id = racineSection.getAttribute('data-sbc-cart-section');
    const conteneur = racineSection.closest('.shopify-section') || racineSection.parentElement;
    const actif = document.activeElement;
    const marqueur = actif && conteneur && conteneur.contains(actif)
      ? (actif.id || actif.getAttribute('name') || null) : null;
    try {
      const rep = await requete(`${url('cart')}?section_id=${encodeURIComponent(id)}`, {
        headers: { Accept: 'text/html' }
      });
      if (!rep.ok) return;
      const html = await rep.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const neuf = doc.querySelector('[data-sbc-cart-section]');
      if (!neuf) return;
      const ancien = document.querySelector('[data-sbc-cart-section]');
      if (!ancien) return;
      ancien.replaceWith(neuf);
      /* Le focus revient sur le contrôle équivalent du DOM reconstruit : la
         cliente ne doit pas être renvoyée en haut de page après un « + ». */
      if (marqueur) {
        const cible = document.getElementById(marqueur) || document.querySelector(`[name="${marqueur}"]`);
        if (cible) cible.focus({ preventScroll: true });
      }
      /* Le tiroir remet son pied en place sur cet événement : il remplace le
         MutationObserver que le lot 104 supprime. */
      document.dispatchEvent(new CustomEvent('sbc:section-rendue'));
    } catch (_) { /* le panier reste utilisable même si le rendu échoue */ }
  };

  /* --------------------------------------------------------------------
     Réconciliation : décider, appliquer groupé, recommencer si l'état réel
     a changé — trois passes au plus.

     Pourquoi plusieurs passes malgré « une réconciliation = un appel » : le
     thème ne décide jamais qu'un produit est gratuit. Il pose la ligne, puis
     doit CONSTATER dans le panier réel ce que Shopify a facturé. Ce constat
     n'existe qu'après la réponse de Shopify ; une deuxième passe est donc
     nécessaire dans le seul cas où un cadeau vient d'être posé, et elle ne
     produit une opération que si la gratuité a été refusée. Le cas courant
     — panier déjà cohérent — se termine en une passe sans aucun appel.
     -------------------------------------------------------------------- */
  const PASSES_MAX = 3;
  const refus = [];
  let etatPublic = { sousTotal: 0, nombreScoops: 0, palierLivraison: false };

  const appliquer = async (decision, panier) => {
    let courant = panier;
    /* Retraits et quantités partent ensemble, en UNE requête indexée par clé
       de ligne. `/cart/update.js` renvoie le panier complet : la relecture est
       comprise dans l'écriture.

       Le corps est un OBJET indexé par clé, jamais le tableau positionnel
       `{"updates":[1,2]}` — mesuré en V21 : avec une ligne cadeau au panier,
       le tableau scindait la ligne visée au lieu d'en porter la quantité. */
    const updates = {};
    decision.retraits.forEach((r) => { updates[r.key] = 0; });
    decision.quantites.forEach((q) => { updates[q.key] = q.quantity; });
    if (Object.keys(updates).length) {
      courant = await poster('cart/update.js', { updates });
    }
    /* Les propriétés seules : `/cart/change.js` les remplace EN BLOC, d'où la
       carte complète envoyée par la décision. La ligne ne bouge pas, son prix
       non plus. */
    for (const changement of (decision.proprietes || [])) {
      courant = await poster('cart/change.js', {
        id: changement.key, properties: changement.properties
      });
    }
    /* `decision.remplacements` n'est plus jamais alimentée : depuis le lot 148
       (décision D19) le moteur ne bascule plus aucune ligne d'une variante
       « AVEC Vidéo » à sa sœur « SANS ». La liste reste lue — vide — pour
       qu'un remplacement qui reviendrait se voie ici au lieu de s'appliquer en
       silence dans le dos de la cliente. */
    if ((decision.remplacements || []).length) signaler(new Error('remplacement inattendu'));
    return courant;
  };

  /* Lot 130 — ce que le moteur vient de faire, pour que l'affichage puisse le
     DIRE. Aucune décision ne change. Depuis le lot 147 le thème n'ajoute plus
     aucun cadeau : la liste reste toujours vide. Elle reste publiée dans le
     détail de `sbc:cadeaux` comme trace d'un ajout qui reviendrait — mais
     plus aucun afficheur ne la lit : le narrateur de gestes de
     `assets/sbc-cadeaux.js` a été retiré en clôture de la vague 147-150,
     faute de geste à raconter. */
  let gestesDuCycle = { ajouts: [] };

  const reconcilier = async (panierConnu) => {
    let panier = panierConnu || await lirePanier();
    gestesDuCycle = { ajouts: [] };
    if (!RECONCILIE) return panier;

    for (let passe = 0; passe < PASSES_MAX; passe += 1) {
      const decision = DECISION.decisionCadeaux(panier, CFG, refus);
      decision.refusAAjouter.forEach((cle) => { if (refus.indexOf(cle) === -1) refus.push(cle); });
      etatPublic = decision.etat;

      const rien = !decision.retraits.length && !decision.quantites.length
        && !(decision.proprietes || []).length && !(decision.remplacements || []).length;
      if (rien) return panier;

      let suivant;
      try { suivant = await appliquer(decision, panier); }
      catch (erreur) {
        /* Une mutation refusée (clé périmée, stock) ne gèle pas le cycle : on
           repart de l'état réel. C'était déjà la règle V17. */
        signaler(erreur);
        suivant = null;
      }
      panier = suivant || await lirePanier();
    }
    return panier;
  };

  const signaler = (erreur) => {
    const message = (erreur && erreur.message) || 'Cette action n’a pas pu être appliquée.';
    document.dispatchEvent(new CustomEvent('sbc:panier-erreur', { detail: { message } }));
  };

  const publier = async (panier) => {
    document.querySelectorAll('[data-cart-count]').forEach((n) => {
      n.textContent = panier.item_count;
      n.hidden = panier.item_count === 0;
    });
    document.querySelectorAll('[data-sbc-ouvrir-panier]').forEach((n) => {
      const suffixe = panier.item_count > 1 ? 's' : '';
      n.setAttribute('aria-label', `Panier, ${panier.item_count} produit${suffixe}`);
    });
    if (domEnRetard(panier)) await rendreSection();
    document.dispatchEvent(new CustomEvent('sbc:cadeaux', {
      detail: { panier, etat: { ...etatPublic }, gestes: { ajouts: [...gestesDuCycle.ajouts] } }
    }));
    document.dispatchEvent(new CustomEvent('sbc:cart-updated', { detail: panier }));
  };

  /* --------------------------------------------------------------------
     La file. Une seule opération de panier à la fois, dans l'ordre d'arrivée.
     Chaque tâche reçoit un contexte où elle écrit le panier qu'elle a obtenu ;
     le moteur enchaîne réconciliation et publication.
     -------------------------------------------------------------------- */
  let file = Promise.resolve();
  let enCours = 0;
  let dernierPanier = null;

  const marquerOccupe = (etat) => {
    document.documentElement.toggleAttribute('data-sbc-panier-occupe', etat);
  };

  const enfiler = (tache) => {
    enCours += 1;
    marquerOccupe(true);
    const suite = file.then(async () => {
      let panier = null;
      let echec = null;
      /* Une tâche qui échoue ne saute PAS la réconciliation : le panier réel a
         pu changer avant l'échec, et l'affichage doit suivre l'état réel. */
      try { panier = await tache(); }
      catch (erreur) { echec = erreur; signaler(erreur); }
      try {
        panier = await reconcilier(panier);
        dernierPanier = panier;
        await publier(panier);
      } catch (_) { /* le panier reste utilisable */ }
      /* L'erreur est ensuite rendue à l'appelant : lui seul sait quoi en dire
         à la cliente (couleur refusée, quantité indisponible). */
      if (echec) throw echec;
      return panier;
    }).finally(() => {
      enCours -= 1;
      if (enCours === 0) marquerOccupe(false);
    });
    file = suite.catch(() => null);
    return suite;
  };

  /* --------------------------------------------------------------------
     API publique. Tout le thème passe par là.
     -------------------------------------------------------------------- */
  const ajouter = (items, options) => enfiler(async () => {
    const reponse = await poster('cart/add.js', { items });
    if (!options || options.ouvrirTiroir !== false) {
      const lignes = Array.isArray(reponse?.items) ? reponse.items : (reponse ? [reponse] : []);
      demandeTiroir = {
        items: items.map((demande, index) => {
          const ligne = lignes[index] || {};
          return {
            key: ligne.key || null,
            variantId: ligne.variant_id || ligne.id || demande.id,
            properties: ligne.properties || demande.properties || {}
          };
        })
      };
    }
    return null;
  });

  const changer = (cle, quantite) => enfiler(() => poster('cart/update.js', { updates: { [cle]: quantite } }));

  const mettreAJour = (corps) => enfiler(() => poster('cart/update.js', corps));

  const vider = () => enfiler(() => poster('cart/clear.js', {}));

  const sync = () => enfiler(async () => null);

  /* Debounce de SAISIE — le seul délai du panier, et il ne coordonne rien.
     Il groupe les clics rapides sur « + » / « − » d'une même ligne pour
     n'envoyer qu'une requête. Sans lui, cinq clics font cinq allers-retours et
     la quantité affichée saute. */
  const DEBOUNCE_SAISIE_MS = 180;
  const enAttente = new Map();
  let minuteurSaisie = null;

  const changerBientot = (operations) => {
    operations.forEach(({ cle, quantite }) => enAttente.set(cle, quantite));
    window.clearTimeout(minuteurSaisie);
    minuteurSaisie = window.setTimeout(() => {
      if (!enAttente.size) return;
      const updates = {};
      enAttente.forEach((quantite, cle) => { updates[cle] = quantite; });
      enAttente.clear();
      enfiler(() => poster('cart/update.js', { updates })).catch(() => {});
    }, DEBOUNCE_SAISIE_MS);
  };

  /* Le tiroir s'ouvre après un ajout, quand le moteur a fini — pas après un
     délai. Le drapeau est posé par `ajouter()` et lu à la publication. */
  let demandeTiroir = null;
  document.addEventListener('sbc:cart-updated', () => {
    if (!demandeTiroir) return;
    const ajout = demandeTiroir;
    demandeTiroir = null;
    document.dispatchEvent(new CustomEvent('sbc:panier-ajout-termine', { detail: ajout }));
  });

  /* --------------------------------------------------------------------
     Portes d'entrée du thème : écouteurs délégués, pas d'interception.

     Un formulaire d'ajout classique rechargerait la page ; on le prend en
     charge ici pour rester sur place et ouvrir le tiroir. `data-sbc-sans-ajax`
     laisse un formulaire suivre son chemin natif si un cas l'exige.
     -------------------------------------------------------------------- */
  document.addEventListener('submit', (event) => {
    if (event.defaultPrevented) return;   /* un composant l'a déjà pris en charge */
    const form = event.target.closest('form[action*="/cart/add"]');
    if (!form || form.hasAttribute('data-sbc-sans-ajax')) return;
    event.preventDefault();
    const donnees = new FormData(form);
    const items = [{
      id: Number(donnees.get('id')),
      quantity: Number(donnees.get('quantity')) || 1
    }];
    if (!items[0].id) return;
    const proprietes = {};
    donnees.forEach((valeur, nom) => {
      const trouve = /^properties\[(.+)\]$/.exec(nom);
      if (trouve && String(valeur).length) proprietes[trouve[1]] = valeur;
    });
    if (Object.keys(proprietes).length) items[0].properties = proprietes;
    const bouton = form.querySelector('[type="submit"]');
    if (bouton) bouton.setAttribute('aria-busy', 'true');
    ajouter(items)
      .catch(() => { /* le message est déjà publié sur `sbc:panier-erreur` */ })
      .finally(() => { if (bouton) bouton.removeAttribute('aria-busy'); });
  });

  /* Un panier modifié dans un autre onglet, ou restauré depuis le cache de
     retour arrière, doit être relu — mais UNE fois, à la réapparition de la
     page, et non à chaque `focus` toutes les 150 ms comme avant le lot 104. */
  window.addEventListener('pageshow', (event) => { if (event.persisted) sync().catch(() => {}); });

  window.SbcPanier = {
    lire: lirePanier,
    ajouter,
    changer,
    changerBientot,
    mettreAJour,
    vider,
    sync,
    enfiler,
    etat: () => ({ ...etatPublic }),
    panier: () => dernierPanier,
    occupe: () => enCours > 0
  };

  /* Premier cycle : le panier rendu par Liquid peut être en retard sur l'état
     réel (retour arrière, panier modifié ailleurs). Une seule vérification au
     chargement, sans délai. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { sync().catch(() => {}); }, { once: true });
  } else {
    sync().catch(() => {});
  }
})();
